/*
Purpose: Day view — one swimlane per tutor with a compressed per-tutor timeline.
*/

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import {
  DENSITY,
  DIAGNOSTICS_LANE_COLOR,
  SWIMLANE_HOUR_MIN_WIDTH_PX,
  SWIMLANE_STACK_ROW_HEIGHT_PX,
  TUTOR_LANE_COLORS,
} from '../constants';
import {
  buildTutorTimelineSegments,
  isHourInPast,
  resolveTimelineFocusHour,
} from '../session-model';
import SessionCard from './SessionCard';

const LANE_HEAD_WIDTH = 200;

function sessionSegmentEndHour(segment) {
  const ends = segment.sessions.map((session) =>
    Math.ceil(
      session.endHour != null && session.endHour > session.startHour
        ? session.endHour
        : session.startHour + 1
    )
  );
  return Math.max(segment.hour + 1, ...ends);
}

/**
 * Scroll the day swimlane so the current / upcoming session card sits just after
 * the sticky tutor column. Prefers session columns; falls back to empty hour slots.
 */
function scrollSwimlaneToFocusHour(scroller, focusHour) {
  if (!scroller) {
    return false;
  }

  const segments = Array.from(scroller.querySelectorAll('[data-cv2-seg-hour]'));
  if (segments.length === 0) {
    return false;
  }

  function coversFocus(el) {
    const start = Number(el.getAttribute('data-cv2-seg-hour'));
    const endAttr = el.getAttribute('data-cv2-seg-end');
    if (endAttr != null && endAttr !== '') {
      const end = Number(endAttr);
      return start <= focusHour && focusHour < end;
    }
    return start === focusHour;
  }

  function leftmost(list) {
    if (!list || list.length === 0) {
      return null;
    }
    let best = list[0];
    let bestLeft = best.getBoundingClientRect().left;
    list.forEach((el) => {
      const left = el.getBoundingClientRect().left;
      if (left < bestLeft) {
        bestLeft = left;
        best = el;
      }
    });
    return best;
  }

  const sessionSegments = segments.filter(
    (el) => el.getAttribute('data-cv2-seg-type') === 'session'
  );

  let best = leftmost(sessionSegments.filter(coversFocus));

  if (!best && sessionSegments.length > 0) {
    const later = sessionSegments
      .filter((el) => Number(el.getAttribute('data-cv2-seg-hour')) >= focusHour)
      .sort(
        (a, b) =>
          Number(a.getAttribute('data-cv2-seg-hour')) -
          Number(b.getAttribute('data-cv2-seg-hour'))
      );
    if (later.length > 0) {
      const nextHour = Number(later[0].getAttribute('data-cv2-seg-hour'));
      best = leftmost(
        sessionSegments.filter(
          (el) => Number(el.getAttribute('data-cv2-seg-hour')) === nextHour
        )
      );
    } else {
      // All sessions are earlier than focus — show the latest session group.
      const hours = sessionSegments
        .map((el) => Number(el.getAttribute('data-cv2-seg-hour')))
        .sort((a, b) => b - a);
      best = leftmost(
        sessionSegments.filter(
          (el) => Number(el.getAttribute('data-cv2-seg-hour')) === hours[0]
        )
      );
    }
  }

  if (!best) {
    best = leftmost(segments.filter(coversFocus)) || leftmost(segments);
  }

  if (!best) {
    return false;
  }

  const rect = best.getBoundingClientRect();
  if (rect.width < 2) {
    return false;
  }

  const scrollerLeft = scroller.getBoundingClientRect().left;
  const delta = rect.left - scrollerLeft - LANE_HEAD_WIDTH;
  scroller.scrollLeft = Math.max(0, scroller.scrollLeft + delta);
  return true;
}

/**
 * @param {Object} props
 * @param {Object[]} props.lanes Tutor lanes from `buildTutorLanes`.
 * @param {{startHour:number, endHour:number}} props.hourWindow Inclusive-exclusive column range.
 * @param {string} props.selectedDate `YYYY-MM-DD` for the day being shown (past-hour checks).
 * @param {string} props.density One of `DENSITY`.
 * @param {Function} props.onCreateInSlot Called with `{ tutorKey, tutorName, startHour }` when
 *   a future empty hour cell is clicked.
 */
export default function SwimlaneDayView({
  lanes,
  hourWindow,
  selectedDate,
  density,
  onOpenSession,
  onStatusClick,
  onModeClick,
  onCreateInSlot,
  onStudentClick,
  onTutorClick,
}) {
  const swimlaneRef = useRef(null);
  const autoScrollKeyRef = useRef('');
  const cardVariant = density === DENSITY.COMPACT ? 'compact' : 'detailed';
  const stackRowHeight =
    density === DENSITY.COMPACT
      ? Math.round(SWIMLANE_STACK_ROW_HEIGHT_PX * 0.82)
      : SWIMLANE_STACK_ROW_HEIGHT_PX;

  useEffect(() => {
    const allSessions = lanes.reduce(
      (list, lane) => list.concat(lane.sessions || []),
      []
    );
    const focusHour = resolveTimelineFocusHour(selectedDate, hourWindow, allSessions);
    const sessionCount = allSessions.length;
    const scrollKey = `${selectedDate}|${hourWindow.startHour}|${hourWindow.endHour}|${density}|${focusHour}|${sessionCount}`;

    if (autoScrollKeyRef.current === scrollKey) {
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 12;

    const tryScroll = () => {
      if (cancelled) {
        return;
      }
      const scroller = swimlaneRef.current;
      const ok = scrollSwimlaneToFocusHour(scroller, focusHour);
      if (ok) {
        autoScrollKeyRef.current = scrollKey;
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        requestAnimationFrame(tryScroll);
      }
    };

    const frame = requestAnimationFrame(tryScroll);
    const timer = window.setTimeout(tryScroll, 80);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [lanes, hourWindow, selectedDate, density]);

  return (
    <div className="cv2-swimlane" ref={swimlaneRef}>
      <div className="cv2-swimlane__grid">
        <div
          className="cv2-swimlane__head"
          style={{ gridTemplateColumns: `${LANE_HEAD_WIDTH}px minmax(0, 1fr)` }}
        >
          <div className="cv2-swimlane__head-cell">Tutor</div>
          <div className="cv2-swimlane__head-cell cv2-swimlane__head-cell--timeline">
            Timeline
          </div>
        </div>

        {lanes.map((lane) => {
          const laneColor = lane.isDiagnostics
            ? DIAGNOSTICS_LANE_COLOR
            : TUTOR_LANE_COLORS[lane.colorIndex % TUTOR_LANE_COLORS.length];
          const segments = buildTutorTimelineSegments(lane.sessions, hourWindow);
          const columnTracks =
            segments.length > 0
              ? segments
                  .map((segment) => {
                    // Session column hugs the card. Empty hours keep a fixed slot.
                    const width =
                      segment.type === 'session'
                        ? 'max-content'
                        : `${SWIMLANE_HOUR_MIN_WIDTH_PX}px`;
                    return width;
                  })
                  .join(' ')
              : `${SWIMLANE_HOUR_MIN_WIDTH_PX}px`;
          const gridTemplateColumns = `${LANE_HEAD_WIDTH}px ${columnTracks}`;
          const maxStack = segments.reduce(
            (max, segment) => Math.max(max, segment.sessions.length || 1),
            1
          );
          const emptySlotMinHeight = Math.max(maxStack * stackRowHeight + 28, stackRowHeight + 28);

          return (
            <div className="cv2-lane" style={{ gridTemplateColumns }} key={lane.tutorKey}>
              <div className="cv2-lane__head">
                {lane.isDiagnostics ? (
                  <span
                    className="cv2-lane__avatar"
                    style={{ background: laneColor.edge }}
                    title={lane.tutorName}
                  >
                    ID
                  </span>
                ) : (
                  <button
                    type="button"
                    className="cv2-lane__avatar"
                    style={{ background: laneColor.edge }}
                    title={`Open ${lane.tutorName}`}
                    onClick={() =>
                      onTutorClick({ tutorKey: lane.tutorKey, tutorName: lane.tutorName })
                    }
                  >
                    {lane.initials}
                  </button>
                )}
                <span className="cv2-lane__meta">
                  {lane.isDiagnostics ? (
                    <span className="cv2-lane__name" title={lane.tutorName}>
                      {lane.tutorName}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="cv2-lane__name"
                      title={lane.tutorName}
                      onClick={() =>
                        onTutorClick({ tutorKey: lane.tutorKey, tutorName: lane.tutorName })
                      }
                    >
                      {lane.tutorName}
                    </button>
                  )}
                  <span className="cv2-lane__sub" title={lane.subjects.join(', ')}>
                    {lane.sessionCount} session{lane.sessionCount === 1 ? '' : 's'}
                    {lane.lessonCount
                      ? ` · ${lane.lessonCount} student${lane.lessonCount === 1 ? '' : 's'}`
                      : ''}
                    {lane.subjects.length ? ` · ${lane.subjects.join(', ')}` : ''}
                  </span>
                </span>
              </div>

              {segments.map((segment) => {
                if (segment.type === 'empty') {
                  const isPast = isHourInPast(selectedDate, segment.hour);
                  const canCreate = !isPast && !lane.isDiagnostics;

                  return (
                    <div
                      key={segment.key}
                      className={[
                        'cv2-lane__segment',
                        'cv2-slot',
                        isPast ? 'cv2-slot--past' : 'cv2-slot--empty',
                        canCreate ? '' : 'cv2-slot--disabled',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={{ minHeight: emptySlotMinHeight }}
                      data-cv2-seg-hour={segment.hour}
                      data-cv2-seg-type="empty"
                      role={canCreate ? 'button' : undefined}
                      tabIndex={canCreate ? 0 : undefined}
                      title={isPast ? 'This time has already passed' : 'Create session'}
                      onClick={
                        canCreate
                          ? () =>
                            onCreateInSlot({
                              tutorKey: lane.tutorKey,
                              tutorName: lane.tutorName,
                              startHour: segment.hour,
                            })
                          : undefined
                      }
                      onKeyDown={
                        canCreate
                          ? (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onCreateInSlot({
                                tutorKey: lane.tutorKey,
                                tutorName: lane.tutorName,
                                startHour: segment.hour,
                              });
                            }
                          }
                          : undefined
                      }
                    >
                      <div className="cv2-lane__segment-label">{segment.label}</div>
                    </div>
                  );
                }

                return (
                  <div
                    key={segment.key}
                    className="cv2-lane__segment cv2-lane__segment--session"
                    data-cv2-seg-hour={segment.hour}
                    data-cv2-seg-end={sessionSegmentEndHour(segment)}
                    data-cv2-seg-type="session"
                  >
                    <div className="cv2-lane__segment-label" title={segment.label}>
                      {segment.label}
                    </div>
                    <div className="cv2-lane__segment-cards">
                      {segment.sessions.map((session) => (
                        <div className="cv2-lane__card-wrap" key={session.key}>
                          <SessionCard
                            session={session}
                            variant={cardVariant}
                            onOpen={onOpenSession}
                            onStatusClick={onStatusClick}
                            onModeClick={onModeClick}
                            onStudentClick={onStudentClick}
                            onTutorClick={onTutorClick}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

SwimlaneDayView.propTypes = {
  lanes: PropTypes.arrayOf(
    PropTypes.shape({
      tutorKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      tutorName: PropTypes.string.isRequired,
      initials: PropTypes.string.isRequired,
      colorIndex: PropTypes.number.isRequired,
      sessions: PropTypes.array.isRequired,
      sessionCount: PropTypes.number,
      lessonCount: PropTypes.number.isRequired,
      subjects: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ).isRequired,
  hourWindow: PropTypes.shape({
    startHour: PropTypes.number.isRequired,
    endHour: PropTypes.number.isRequired,
  }).isRequired,
  selectedDate: PropTypes.string.isRequired,
  density: PropTypes.string.isRequired,
  onOpenSession: PropTypes.func.isRequired,
  onStatusClick: PropTypes.func.isRequired,
  onModeClick: PropTypes.func.isRequired,
  onCreateInSlot: PropTypes.func.isRequired,
  onStudentClick: PropTypes.func.isRequired,
  onTutorClick: PropTypes.func.isRequired,
};
